import type { ButtonHTMLAttributes, CSSProperties, HTMLAttributes, ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, BadgeCheck, Crown, Frown, LoaderCircle, MapPin, RefreshCw, Sparkles } from "lucide-react";
import { NetworkIcon } from "./brand-icons";
import { NETWORK_LABELS } from "../lib/networks";
import type { CreatorSummary } from "../lib/api";
import { classNames, initials, formatScore } from "../lib/format";

export function Brand({ compact = false, inverse = false }: { compact?: boolean; inverse?: boolean }) {
  return (
    <Link to="/" className={classNames("inline-flex items-center gap-2.5 text-lg font-black tracking-[-.05em]", inverse && "text-white")} aria-label="Score Creator — início">
      <span className="block size-[38px] shrink-0" aria-hidden="true"><img className="size-full object-contain" src="/brand/logo-mark-clean-512.png" alt="" width="38" height="38" /></span>
      {!compact && <span>score<span className={inverse ? "text-lime" : "text-forest"}>creator</span></span>}
    </Link>
  );
}

export type ButtonVariant = "primary" | "secondary" | "ghost" | "dark" | "danger";

const buttonBaseClass = "button inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[11px] border border-transparent font-extrabold leading-none transition-[transform,box-shadow,background,border-color,color] duration-150 enabled:hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-50";
const buttonVariantClasses: Record<ButtonVariant, string> = {
  primary: "bg-forest text-white shadow-brand enabled:hover:bg-[#52007b] enabled:hover:shadow-[0_12px_28px_rgba(102,0,153,.28)]",
  secondary: "border-[#cec3d3] bg-surface-raised text-ink enabled:hover:border-forest enabled:hover:text-forest",
  ghost: "bg-transparent text-inherit enabled:hover:bg-[rgba(102,0,153,.08)]",
  dark: "bg-[#211925] text-white shadow-none",
  danger: "border-[#f1c7c4] bg-transparent text-danger",
};
const buttonSizeClasses = {
  sm: "min-h-[38px] px-3.5 text-[13px]",
  md: "min-h-[45px] px-[19px] text-sm",
  lg: "min-h-[52px] px-6 text-[15px]",
} as const;

export function buttonClass(variant: ButtonVariant = "primary", size: "sm" | "md" | "lg" = "md") {
  return classNames(buttonBaseClass, buttonVariantClasses[variant], buttonSizeClasses[size]);
}

export function Button({ variant = "primary", size = "md", className, children, loading, ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant; size?: "sm" | "md" | "lg"; loading?: boolean }) {
  return (
    <button className={classNames(buttonClass(variant, size), className)} disabled={loading || props.disabled} {...props}>
      {loading && <LoaderCircle size={16} className="animate-spin" aria-hidden="true" />}
      {children}
    </button>
  );
}

export function Eyebrow({ children, tone = "green" }: { children: ReactNode; tone?: "green" | "lime" | "coral" }) {
  const tones = { green: "text-forest", lime: "text-lime", coral: "text-coral" } as const;
  return <span className={classNames("eyebrow inline-flex items-center gap-[7px] text-xs leading-[1.35] font-black tracking-[.12em] uppercase", tones[tone])}>{children}</span>;
}

export function Card({ className, children, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={classNames("card rounded-card border border-line bg-surface", className)} {...props}>{children}</div>;
}

export function Avatar({ name, src, size = "md" }: { name: string; src?: string | null; size?: "sm" | "md" | "lg" | "xl" }) {
  const sizes = { sm: "size-[34px] text-[13px]", md: "size-[46px] text-[13px]", lg: "size-[62px] text-base", xl: "size-[88px] text-[22px]" } as const;
  const avatarClass = classNames("avatar shrink-0 rounded-full object-cover", sizes[size]);
  return src ? <img className={avatarClass} src={src} alt={`Foto de ${name}`} /> : <span className={classNames(avatarClass, "avatar--fallback inline-grid place-items-center bg-[linear-gradient(145deg,#d8b7f2,#b77bd2)] font-black text-[#2a1631]")} aria-label={`Iniciais de ${name}`}>{initials(name)}</span>;
}

export function ScoreBadge({ value, small = false }: { value: number; small?: boolean }) {
  return <span className={classNames("inline-flex items-center gap-1 rounded-lg bg-[#261c2a] px-[9px] py-1.5 text-[13px] font-black text-lime", small && "text-sm")} aria-label={`Creator Score ${formatScore(value)} de 100`}><Sparkles size={small ? 12 : 14} aria-hidden="true" /> {formatScore(value)}</span>;
}

export function ScoreRing({ value, size = 140, label = "Creator Score" }: { value: number; size?: number; label?: string }) {
  const normalized = Math.min(100, Math.max(0, value));
  return (
    <div className="score-ring grid shrink-0 place-items-center rounded-full p-[7px] shadow-[0_12px_40px_rgba(0,0,0,.16)]" style={{ width: size, height: size, "--score-ring-size": `${size}px`, background: `conic-gradient(var(--lime) ${normalized * 3.6}deg, rgba(255,255,255,.12) 0deg)` } as CSSProperties} role="img" aria-label={`${label}: ${formatScore(value)} de 100`}>
      <div className="score-ring__inside grid size-full min-h-0 min-w-0 content-center justify-items-center rounded-full border-4 border-ink bg-ink text-center text-white">
        <strong className="text-[max(22px,calc(var(--score-ring-size,140px)*.25))] leading-[.95] tracking-[-.06em] text-lime">{formatScore(value)}</strong>
        <span className="mt-1.5 max-w-[76%] text-[max(12px,calc(var(--score-ring-size,140px)*.08))] leading-[1.15] tracking-[.05em] uppercase">{label}</span>
      </div>
    </div>
  );
}

export function CreatorCard({ creator, rank, onOpen, searchEventId }: { creator: CreatorSummary; rank?: number; onOpen?: (creator: CreatorSummary) => void; searchEventId?: string | null }) {
  const context = new URLSearchParams();
  if (creator.featuredCampaignId) context.set("featured", creator.featuredCampaignId);
  if (searchEventId) context.set("search", searchEventId);
  if (!creator.featuredCampaignId && !searchEventId) context.set("source", "internal");
  const query = context.toString();
  return (
    <article className="overflow-hidden rounded-card border border-[rgba(102,0,153,.34)] bg-surface shadow-card-sm transition-[transform,box-shadow] duration-200 hover:-translate-y-[5px] hover:shadow-card">
      <Link to={`/criador/${creator.slug}${query ? `?${query}` : ""}`} className="block p-[18px]" aria-label={`Ver perfil de ${creator.name}`} onClick={() => onOpen?.(creator)}>
        <div className="flex min-h-7 items-center justify-between">
          {rank ? <span className="text-[15px] font-black text-[#625b67]">{String(rank).padStart(2, "0")}</span> : <span className="rounded-[30px] bg-[#e8dcef] px-[9px] py-[5px] text-xs leading-[1.25] font-black tracking-[.08em] text-[#5f2478] uppercase">{creator.category}</span>}
          <div className="flex items-center gap-1.5">{creator.isPremium && <span className="inline-flex items-center gap-1 rounded-[20px] border border-[#d3bfdc] bg-[#e8dcef] px-2 py-[5px] text-xs leading-[1.25] font-black tracking-[.05em] text-[#5f2478] uppercase [&_svg]:size-3" aria-label="Perfil Premium"><Crown aria-hidden="true" /> Premium</span>}<ScoreBadge value={creator.score} small /></div>
        </div>
        <div className="my-[25px] flex items-center gap-3">
          <Avatar name={creator.name} src={creator.avatarUrl} size="lg" />
          <div>
            <h3 className="mb-1 flex items-center gap-1 text-lg tracking-[-.03em]">{creator.name} {creator.isVerified && <BadgeCheck size={17} className="text-forest" aria-label="Perfil verificado" />}</h3>
            <p className="m-0 text-xs leading-[1.45] text-muted">{creator.headline}</p>
          </div>
        </div>
        {Boolean(creator.networks?.length) && <div className="-mt-3 mb-3.5 ml-[76px] flex items-center gap-1.5 text-forest" aria-label="Redes cadastradas">{creator.networks?.map((network) => <span className="inline-flex size-6 items-center justify-center rounded-full border border-line bg-cream [&_svg]:size-[13px]" key={network} title={NETWORK_LABELS[network]}><NetworkIcon network={network} size={16} /><span className="sr-only">{NETWORK_LABELS[network]}</span></span>)}</div>}
        <div className="flex justify-between gap-3 border-t border-line pt-[13px] text-[13px] text-muted [&>span]:inline-flex [&>span]:items-center [&>span]:gap-1">
          <span><MapPin size={14} aria-hidden="true" /> {creator.city}, {creator.state}</span>
          <span className="font-extrabold text-ink">Ver perfil <ArrowUpRight size={15} aria-hidden="true" /></span>
        </div>
      </Link>
    </article>
  );
}

export function MetricCard({ label, value, change, icon }: { label: string; value: string; change?: string; icon?: ReactNode }) {
  return (
    <Card className="metric-card min-h-[125px] p-4">
      <div className="flex items-center justify-between text-[13px] leading-normal text-muted [&_svg]:w-[17px] [&_svg]:text-forest"><span>{label}</span>{icon}</div>
      <strong className="my-[14px] block text-[30px] tracking-[-.05em]">{value}</strong>
      {change && <small className="text-xs text-success">{change}</small>}
    </Card>
  );
}

const stateCardClass = "state-card grid min-h-[220px] place-content-center justify-items-center gap-[7px] rounded-[14px] border border-dashed border-[#c9cfc9] bg-surface px-[22px] py-[34px] text-center text-muted shadow-card-sm [&>svg]:text-forest [&>strong]:text-lg [&>strong]:text-ink [&>span]:text-sm [&>span]:leading-[1.55]";

export function LoadingState({ label = "Carregando" }: { label?: string }) {
  return <div className={stateCardClass} role="status"><LoaderCircle className="animate-spin" aria-hidden="true" /><strong>{label}</strong><span>Só um instante.</span></div>;
}

export function ErrorState({ title = "Algo saiu do ritmo", message = "Não conseguimos carregar estes dados agora.", onRetry }: { title?: string; message?: string; onRetry?: () => void }) {
  return (
    <div className={classNames(stateCardClass, "border-[#e4bcb4] bg-[#fff8f5] [&>svg]:text-coral")} role="alert">
      <Frown aria-hidden="true" />
      <strong>{title}</strong>
      <span>{message}</span>
      {onRetry && <Button className="mt-2" variant="secondary" size="sm" onClick={onRetry}><RefreshCw size={14} aria-hidden="true" /> Tentar de novo</Button>}
    </div>
  );
}

export function EmptyState({ title, message, action, className }: { title: string; message: string; action?: ReactNode; className?: string }) {
  return <div className={classNames(stateCardClass, className)}><span className="size-8 bg-[radial-gradient(circle,var(--lime)_0_35%,var(--ink)_37%_44%,transparent_46%)]" aria-hidden="true" /><strong>{title}</strong><span>{message}</span>{action}</div>;
}

export function SkeletonGrid({ count = 3 }: { count?: number }) {
  return <div className="grid grid-cols-1 gap-4 min-[560px]:grid-cols-2 min-[820px]:grid-cols-3" aria-label="Carregando perfis">{Array.from({ length: count }, (_, index) => <div key={index} className="grid h-[230px] content-end gap-[11px] rounded-[14px] border border-line bg-surface p-5 [&_span]:h-4 [&_span]:animate-[shimmer_1.3s_infinite] [&_span]:rounded-[10px] [&_span]:bg-[linear-gradient(90deg,#eeeae2,#f9f7f2,#eeeae2)] [&_span]:bg-[length:200%] [&_span:first-child]:size-[58px] [&_span:first-child]:rounded-full [&_span:last-child]:w-[65%]"><span /><span /><span /></div>)}</div>;
}

export function FieldError({ children }: { children?: ReactNode }) {
  return children ? <span className="text-[13px] text-danger" role="alert">{children}</span> : null;
}

export function Breadcrumbs({ items }: { items: Array<{ label: string; to?: string }> }) {
  return (
    <nav className="mb-[35px]" aria-label="Navegação estrutural">
      <ol className="m-0 flex list-none flex-wrap gap-2 p-0 text-xs leading-normal text-muted">
        {items.map((item, index) => <li className="not-last:after:ml-2 not-last:after:text-[#b0b8b4] not-last:after:content-['/']" key={`${item.label}-${index}`}>{item.to ? <Link className="hover:text-forest" to={item.to}>{item.label}</Link> : <span aria-current="page">{item.label}</span>}</li>)}
      </ol>
    </nav>
  );
}
